import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import { Elements } from '@stripe/react-stripe-js';
import stripePromise from '../stripe';
import PaymentForm from '../components/PaymentForm';
import { 
  CreditCard, 
  Calendar, 
  CheckCircle2, 
  AlertCircle, 
  Crown,
  ArrowLeft,
  Zap,
  Users,
  BarChart3,
  Shield,
  Star
} from 'lucide-react';

const Subscription = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { 
    subscription, 
    loading, 
    isTrialActive, 
    isSubscriptionActive, 
    getDaysLeftInTrial,
    isSpecialAccount,
    getSubscriptionStatus
  } = useSubscription();
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-purple-800 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  const daysLeft = getDaysLeftInTrial();
  const status = getSubscriptionStatus();

  const handlePaymentSuccess = () => {
    setShowPaymentForm(false);
    // You could show a success message or redirect
  };

  const handlePaymentCancel = () => {
    setShowPaymentForm(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-purple-800 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        {/* <div className="flex items-center mb-8">
          <button
            onClick={() => navigate('/companies')}
            className="flex items-center text-white/80 hover:text-white transition-colors mr-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
        </div> */}
{/* 
        <div className="text-center mb-12">
          <h1 className="text-4xl font-light text-white mb-4">Subscription & Billing</h1>
          <p className="text-white/80 text-lg">Manage your DeckLink subscription</p>
          <button
            onClick={() => navigate('/subscription-demo')}
            className="mt-4 text-blue-200 hover:text-blue-100 text-sm underline"
          >
            View Demo
          </button>
        </div> */}

        {/* Current Status */}
        <div className="bg-white/90 backdrop-blur rounded-xl shadow-xl p-8 mb-8 border border-white/10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-light text-gray-900">Current Plan</h2>
            {isSubscriptionActive() && (
              <div className="flex items-center bg-green-100 border border-green-200 text-green-700 px-3 py-1 rounded-full text-sm">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Active
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Plan Details</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Plan:</span>
                  <span className="text-gray-900 font-medium">
                    {isSpecialAccount() ? 'Special Premium' : 'Basic'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Price:</span>
                  <span className="text-gray-900 font-medium">
                    {isSpecialAccount() ? 'Free' : '$5/month'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className="text-gray-900 font-medium">
                    {status.message}
                  </span>
                </div>
                {subscription?.stripeSubscriptionId && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Subscription ID:</span>
                    <span className="text-gray-900 font-mono text-sm">{subscription.stripeSubscriptionId}</span>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Account Status</h3>
              {isSpecialAccount() ? (
                <div className="bg-purple-100 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <Star className="w-5 h-5 text-purple-500 mr-2" />
                    <span className="text-purple-700 font-medium">Special Account</span>
                  </div>
                  <p className="text-purple-700 text-sm">
                    You have unlimited access to all DeckLink features
                  </p>
                </div>
              ) : isTrialActive() ? (
                <div className="bg-blue-100 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <Calendar className="w-5 h-5 text-blue-500 mr-2" />
                    <span className="text-blue-700 font-medium">Free Trial Active</span>
                  </div>
                  <p className="text-blue-700 text-sm mb-3">
                    You have {daysLeft} days left in your free trial
                  </p>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${Math.max(0, (daysLeft / 7) * 100)}%` }}
                    ></div>
                  </div>
                </div>
              ) : subscription?.status === 'active' ? (
                <div className="bg-green-100 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mr-2" />
                    <span className="text-green-700 font-medium">Paid Subscription</span>
                  </div>
                  <p className="text-green-700 text-sm">
                    Your subscription is active and will renew automatically
                  </p>
                </div>
              ) : (
                <div className="bg-red-100 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                    <span className="text-red-700 font-medium">Trial Expired</span>
                  </div>
                  <p className="text-red-700 text-sm">
                    Your trial has expired. Upgrade to continue using DeckLink.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="bg-white/90 backdrop-blur rounded-xl shadow-xl p-8 mb-8 border border-white/10">
          <h2 className="text-2xl font-light text-gray-900 mb-6">What's Included</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start space-x-3">
              <Zap className="w-6 h-6 text-blue-500 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-gray-900 font-medium mb-1">Unlimited Uploads</h3>
                <p className="text-gray-600 text-sm">Upload unlimited pitch decks and documents</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <BarChart3 className="w-6 h-6 text-blue-500 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-gray-900 font-medium mb-1">Advanced Analytics</h3>
                <p className="text-gray-600 text-sm">Track viewer engagement and insights</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Crown className="w-6 h-6 text-blue-500 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-gray-900 font-medium mb-1">Custom Branding</h3>
                <p className="text-gray-600 text-sm">Add your logo and customize colors</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Users className="w-6 h-6 text-blue-500 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-gray-900 font-medium mb-1">Team Collaboration</h3>
                <p className="text-gray-600 text-sm">Invite team members to collaborate</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Shield className="w-6 h-6 text-blue-500 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-gray-900 font-medium mb-1">Priority Support</h3>
                <p className="text-gray-600 text-sm">Get help when you need it most</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CreditCard className="w-6 h-6 text-blue-500 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-gray-900 font-medium mb-1">Secure Payments</h3>
                <p className="text-gray-600 text-sm">Bank-level security for all transactions</p>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Section - Only show for non-special accounts */}
        {!isSpecialAccount() && !isSubscriptionActive() && (
          <div className="bg-white/90 backdrop-blur rounded-xl shadow-xl p-8 border border-white/10">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-light text-gray-900 mb-2">Upgrade to Pro</h2>
              <p className="text-gray-600">Get unlimited access to all features</p>
            </div>

            {showPaymentForm ? (
              <div className="relative z-30">
                <Elements stripe={stripePromise}>
                  <PaymentForm 
                    onSuccess={handlePaymentSuccess}
                    onCancel={handlePaymentCancel}
                  />
                </Elements>
              </div>
            ) : (
              <div className="text-center">
                <button
                  onClick={() => setShowPaymentForm(true)}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-3 rounded-lg font-medium transition-all"
                >
                  Start Subscription - $5/month
                </button>
                <p className="text-gray-500 text-sm mt-4">
                  Cancel anytime. No commitment required.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Subscription; 